import { useState, type MouseEvent, type ReactNode } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart'
import BarChartIcon from '@mui/icons-material/BarChart'
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart'
import CheckIcon from '@mui/icons-material/Check'
import { useChartKnobsState, type ChartType, type Facet } from '~/state/useChartKnobsState'
import { availableFacetsForChartType, FACET_OPTIONS } from './facets'
import type { AdditivityVerdict } from './seriesAdditivity'
import { CONTROLS } from './chrome/chartChrome'

const FACET_ICONS: Record<Facet, ReactNode> = {
  line: <ShowChartIcon fontSize="small" />,
  stacked_area: <StackedLineChartIcon fontSize="small" />,
  bar: <BarChartIcon fontSize="small" />,
  stacked_bar: <StackedBarChartIcon fontSize="small" />,
}

interface Props {
  pinId: string
  chartType: ChartType
  /** chart_registry.default_facet / page decompFacet — MUST mirror the ChartCanvas
   *  useChartKnobsState call so menu state and chart render share one "default". */
  defaultFacet?: string | null
  /** Chart-semantics doctrine (spec 2026-08-03 §1) — REQUIRED: the resolved verdict for this
   *  chart's fact/kpi/groupBy/effective-VTs (via useSeriesAdditivity). Gates stacked facets —
   *  no more ambient allow-all inferred from `groups`. Verdict objects have no referential
   *  stability across renders; key off `.additive`/`.reason`, never object identity. */
  additivity: AdditivityVerdict
  /** Task 4: any rendered value below zero (ChartCanvas-computed) — gates Show Percentage
   *  alongside `additivity.additive` (a per-row percent-of-total is meaningless with mixed
   *  signs). */
  hasNegativeValues: boolean
}

/** Task 4: additivity.reason -> the stacked-facet denial tooltip copy. 'ok' never renders
 *  (only reached when a facet is actually denied, i.e. additive === false). */
const STACKED_DENIED_TOOLTIP: Record<AdditivityVerdict['reason'], string> = {
  ok: '',
  'vt-heterogeneous': CONTROLS.knobStackedDeniedVt,
  'dim-rule': CONTROLS.knobStackedDeniedDim,
  'metadata-pending': CONTROLS.knobStackedDeniedPending,
}

export function Knobs({ pinId, chartType, defaultFacet, additivity, hasNegativeValues }: Props) {
  const knobs = useChartKnobsState(pinId, chartType, defaultFacet)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const open = Boolean(anchor)

  const allStackable = additivity.additive
  const allowedFacets = availableFacetsForChartType(chartType)
  const showPercentageVisible = chartType === 'absolute_bar'
  const cumulativeForced = FACET_OPTIONS[knobs.facet].isStacked

  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)
  const handleClose = () => setAnchor(null)

  return (
    <>
      <IconButton onClick={handleOpen} size="small" aria-label="chart knobs">
        {FACET_ICONS[knobs.facet]}
      </IconButton>
      <Menu open={open} anchorEl={anchor} onClose={handleClose}>
        {allowedFacets.map((f) => {
          const opt = FACET_OPTIONS[f]
          const stackedAndUnsupported = opt.isStacked && !allStackable
          const item = (
            <MenuItem
              key={f}
              disabled={stackedAndUnsupported}
              onClick={() => {
                knobs.setFacet(f)
              }}
            >
              <ListItemIcon>{FACET_ICONS[f]}</ListItemIcon>
              <ListItemText>{opt.label}</ListItemText>
              {knobs.facet === f ? <CheckIcon fontSize="small" color="primary" /> : null}
            </MenuItem>
          )
          // FU-57: wrap ONLY denied items. A <span> in the menu list removes the item from
          // MenuList's sibling arrow-key traversal, so enabled items must stay direct children;
          // the span is still required on disabled ones (no pointer events → tooltip anchor).
          return stackedAndUnsupported ? (
            <Tooltip key={f} title={STACKED_DENIED_TOOLTIP[additivity.reason]} placement="left">
              <span>{item}</span>
            </Tooltip>
          ) : (
            item
          )
        })}
        <Divider />
        {(() => {
          const item = (
            <MenuItem
              disabled={cumulativeForced}
              onClick={() => knobs.setCumulative(!knobs.cumulative)}
            >
              <ListItemText>{CONTROLS.knobCumulative}</ListItemText>
              {knobs.cumulative ? <CheckIcon fontSize="small" color="primary" /> : null}
            </MenuItem>
          )
          return cumulativeForced ? (
            <Tooltip title={CONTROLS.knobCumulativeForced} placement="left">
              <span>{item}</span>
            </Tooltip>
          ) : (
            item
          )
        })()}
        {chartType === 'timeline' ? (
          <Tooltip title={CONTROLS.knobCumulativeTooltip} placement="left">
            <MenuItem onClick={() => knobs.setRunningTotal(!knobs.runningTotal)}>
              <ListItemText>{CONTROLS.knobRunningTotal}</ListItemText>
              {knobs.runningTotal ? <CheckIcon fontSize="small" color="primary" /> : null}
            </MenuItem>
          </Tooltip>
        ) : null}
        {showPercentageVisible
          ? (() => {
              const denied = !additivity.additive || hasNegativeValues
              const item = (
                <MenuItem
                  disabled={denied}
                  onClick={() => knobs.setShowPercentage(!knobs.showPercentage)}
                >
                  <ListItemText>{CONTROLS.knobShowPercentage}</ListItemText>
                  {knobs.showPercentage ? <CheckIcon fontSize="small" color="primary" /> : null}
                </MenuItem>
              )
              return (
                <>
                  <Divider />
                  {denied ? (
                    <Tooltip
                      title={
                        !additivity.additive
                          ? CONTROLS.knobPercentageDeniedAdditivity
                          : CONTROLS.knobPercentageDeniedNegative
                      }
                      placement="left"
                    >
                      <span>{item}</span>
                    </Tooltip>
                  ) : (
                    item
                  )}
                </>
              )
            })()
          : null}
      </Menu>
    </>
  )
}
